import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "../components/Top3";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).toUpperCase();
};

const SallyHistory = () => {
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    try {
      const t = await api.assistantHistory();
      setTurns(t);
    } catch {
      toast("Could not load history");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const deleteSession = async (sid) => {
    // Two-tap confirm — first click stages, second click within 4s deletes
    if (confirmId !== sid) {
      setConfirmId(sid);
      setTimeout(() => setConfirmId((c) => (c === sid ? null : c)), 4000);
      return;
    }
    try {
      const res = await api.deleteAssistantSession(sid);
      toast("Session cleared", { description: `${res.deleted} turns removed` });
      setConfirmId(null);
      setTurns((rows) => rows.filter((t) => t.session_id !== sid));
    } catch (e) {
      toast("Delete failed", { description: e?.response?.data?.detail || "" });
    }
  };

  // Group turns by session_id, preserve newest-first session order
  const sessions = useMemo(() => {
    const bySession = new Map();
    // turns from API are newest-first; iterate in reverse to build chronological turn lists
    const chrono = [...turns].reverse();
    chrono.forEach((t) => {
      if (!bySession.has(t.session_id)) bySession.set(t.session_id, []);
      bySession.get(t.session_id).push(t);
    });
    // Return sessions sorted by newest turn desc
    return [...bySession.entries()]
      .map(([sid, arr]) => ({
        session_id: sid,
        turns: arr,
        started_at: arr[0]?.created_at,
        ended_at: arr[arr.length - 1]?.created_at,
      }))
      .sort((a, b) => (b.ended_at || "").localeCompare(a.ended_at || ""));
  }, [turns]);

  const filtered = useMemo(() => {
    if (!q.trim()) return sessions;
    const lo = q.toLowerCase();
    return sessions.filter((s) =>
      s.turns.some((t) => (t.text || "").toLowerCase().includes(lo))
    );
  }, [sessions, q]);

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-[1200px] mx-auto space-y-10" data-testid="sally-history-page">
      <header>
        <div className="op-microcopy text-[#00E5FF] mb-4">SALLY · HISTORY</div>
        <h1 className="text-white text-4xl md:text-6xl font-light tracking-tight mb-3">Everything she said.</h1>
        <p className="text-[#A0A5B5] text-lg max-w-2xl">
          Every question, every reply. Grouped by conversation. Search the whole thing.
        </p>
        <div className="op-hairline mt-8" />
      </header>

      <div className="op-panel p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search across every ask and reply…"
          className="op-input"
          data-testid="history-search"
        />
      </div>

      {loading && <div className="op-microcopy op-flicker">LOADING…</div>}

      {!loading && filtered.length === 0 && (
        <div className="op-panel p-8 text-center text-[#5E657D] text-sm" data-testid="history-empty">
          {q ? `No history matches "${q}".` : "No conversations yet. Ask her something."}
        </div>
      )}

      <div className="space-y-8">
        {filtered.map((s, idx) => (
          <section key={s.session_id} data-testid={`history-session-${s.session_id}`}>
            <div className="flex items-start justify-between gap-4 mb-6">
              <SectionHeader
                code={String(idx + 1).padStart(2, "0")}
                label={`SESSION · ${s.session_id.slice(0, 8).toUpperCase()}`}
                caption={`${fmtWhen(s.started_at)} → ${fmtWhen(s.ended_at)} · ${s.turns.length} TURNS`}
              />
              <button
                onClick={() => deleteSession(s.session_id)}
                data-testid={`history-delete-${s.session_id}`}
                className={`op-btn text-[10px] px-3 py-2 inline-flex items-center gap-2 shrink-0 mt-2 ${
                  confirmId === s.session_id
                    ? "border-[#ef4444] text-[#ef4444] bg-[rgba(239,68,68,0.06)]"
                    : "hover:border-[#ef4444]/50 hover:text-[#ef4444]"
                }`}
                title="Delete this conversation"
              >
                <Trash2 size={11} />
                {confirmId === s.session_id ? "TAP AGAIN TO DELETE" : "DELETE"}
              </button>
            </div>
            <div className="op-panel p-6 space-y-4">
              {s.turns.map((t, i) => (
                <div
                  key={i}
                  data-testid={`history-turn-${t.role}-${i}`}
                  className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 border ${
                      t.role === "user"
                        ? "border-white/10 bg-white/[0.03] text-white"
                        : "border-[rgba(0,229,255,0.25)] bg-[rgba(0,229,255,0.03)] text-white"
                    }`}
                  >
                    <div className={`op-tag mb-1 ${t.role === "assistant" ? "text-[#00E5FF]" : ""}`}>
                      {t.role === "user" ? "YOU" : "SALLY"} · {fmtWhen(t.created_at)}
                    </div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">{t.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default SallyHistory;
